import { CirclePlus, Layers3 } from "lucide-react"
import { useEffect } from "react"
import { useStore } from "zustand"

import { Button } from "@/components/button"
import { CardHeading } from "@/components/shared/card-heading"
import { Tag } from "@/components/shared/tag"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { BOSS_SEASONS } from "@/core/talent-tracking/seasons.js"
import { useCreateBossMutation, type NewBossDraft } from "@/hooks/talent-config"
import {
  useTalentConfigRouteStore,
  setPageError,
  setSelectedBossId,
  setSelectedSeason,
  setNewBossDraft,
  updateNewBossDraftField,
} from "@/lib/stores/talent-config-ui-store"
import {
  getTalentConfigBossId,
  type TalentConfigBoss,
  type TalentConfigSeason,
  type TalentConfigSnapshot,
} from "@/lib/talent-config-api"

type BossDraftValidation = {
  name: string | null
  bossId: string | null
}

export function RosterCard({
  snapshot,
}: {
  snapshot: TalentConfigSnapshot | undefined
}) {
  const store = useTalentConfigRouteStore()
  const selectedSeason = useStore(store, (s) => s.selectedSeason)
  const selectedBossId = useStore(store, (s) => s.selectedBossId)
  const newBossDraft = useStore(store, (s) => s.newBossDraft)

  // Reset boss selection if season changes and current boss is not in the new season
  useEffect(() => {
    const selectedSeasonEntry = snapshot?.seasons.find(
      (season) => season.season === selectedSeason,
    )

    if (!selectedSeasonEntry) {
      setSelectedBossId(store, null)
      return
    }

    if (
      !selectedBossId ||
      !selectedSeasonEntry.bossIds.includes(selectedBossId)
    ) {
      setSelectedBossId(store, selectedSeasonEntry.bossIds[0] ?? null)
    }
  }, [selectedBossId, selectedSeason, snapshot, store])

  // Auto-adjust sort order when season changes
  useEffect(() => {
    const seasonBosses = snapshot?.seasons.find(
      (season) => season.season === newBossDraft.season,
    )?.bosses
    const nextSortOrder = seasonBosses?.length ? seasonBosses.length + 1 : 1

    setNewBossDraft(
      store,
      newBossDraft.sortOrder === nextSortOrder
        ? newBossDraft
        : {
            ...newBossDraft,
            sortOrder: nextSortOrder,
          },
    )
  }, [
    newBossDraft.season,
    snapshot,
    newBossDraft.sortOrder,
    store,
    newBossDraft,
  ])

  const createBossMutation = useCreateBossMutation(store)

  const newBossValidation = validateBossDraft(newBossDraft, snapshot)

  const selectedSeasonEntry = snapshot?.seasons.find(
    (season) => season.season === selectedSeason,
  )
  const activeSeasonBosses = selectedSeasonEntry?.bosses ?? []

  return (
    <Card size="sm" className="xl:sticky xl:top-4 xl:self-start">
      <CardHeader>
        <CardHeading
          icon={<Layers3 className="size-4 text-primary" />}
          className="text-lg"
        >
          Season Roster
        </CardHeading>
        <CardDescription>
          Pick a season, then jump into one boss profile at a time.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <CreateBossForm
          draft={newBossDraft}
          validation={newBossValidation}
          isPending={createBossMutation.isPending}
          onSeasonChange={(season) =>
            updateNewBossDraftField(store, "season", season)
          }
          onNameChange={(name) => updateNewBossDraftField(store, "name", name)}
          onSortOrderChange={(sortOrder) =>
            updateNewBossDraftField(store, "sortOrder", sortOrder)
          }
          onSubmit={() => createBossMutation.mutate(newBossDraft)}
        />

        <SeasonSelector
          seasons={BOSS_SEASONS}
          selectedSeason={selectedSeason}
          snapshot={snapshot}
          onSeasonChange={(season) => setSelectedSeason(store, season)}
        />

        <BossList
          bosses={activeSeasonBosses}
          selectedBossId={selectedBossId}
          onSelectBoss={(id) => setSelectedBossId(store, id)}
        />
      </CardContent>
    </Card>
  )
}

function CreateBossForm({
  draft,
  validation,
  isPending,
  onSeasonChange,
  onNameChange,
  onSortOrderChange,
  onSubmit,
}: {
  draft: NewBossDraft
  validation: BossDraftValidation
  isPending: boolean
  onSeasonChange: (season: TalentConfigSeason) => void
  onNameChange: (name: string) => void
  onSortOrderChange: (sortOrder: number) => void
  onSubmit: () => void
}) {
  return (
    <div className="grid gap-3 rounded-[1.4rem] border border-dashed border-primary/20 bg-[color-mix(in_oklch,var(--primary)_4%,var(--card))] p-4">
      <div>
        <div className="text-kicker text-xs text-muted-foreground">
          Create Boss
        </div>
        <div className="mt-1 text-sm text-muted-foreground">
          Add a new boss shell to the selected season, then fill in its tracks.
        </div>
      </div>
      <div className="grid gap-3">
        <div className="grid gap-2">
          <Label htmlFor="new-boss-season">Season</Label>
          <div className="flex flex-wrap gap-2">
            {BOSS_SEASONS.map((season) => (
              <Button
                key={season}
                size="sm"
                type="button"
                variant={draft.season === season ? "default" : "outline"}
                onClick={() => onSeasonChange(season)}
              >
                {season}
              </Button>
            ))}
          </div>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="new-boss-name">Boss Name</Label>
          <Input
            id="new-boss-name"
            value={draft.name}
            onChange={(event) => onNameChange(event.target.value)}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="new-boss-sort-order">Sort Order</Label>
          <Input
            id="new-boss-sort-order"
            type="number"
            min={1}
            value={String(draft.sortOrder)}
            onChange={(event) =>
              onSortOrderChange(Math.max(1, Number(event.target.value || 1)))
            }
          />
        </div>
        <Button
          type="button"
          disabled={
            isPending || Boolean(validation.name || validation.bossId)
          }
          onClick={onSubmit}
        >
          <CirclePlus className="size-4" />
          {isPending ? "Creating boss..." : "Create Boss"}
        </Button>
        {validation.name ? (
          <p className="text-sm text-destructive">{validation.name}</p>
        ) : null}
        {validation.bossId ? (
          <p className="text-sm text-destructive">{validation.bossId}</p>
        ) : null}
      </div>
    </div>
  )
}

function SeasonSelector({
  seasons,
  selectedSeason,
  snapshot,
  onSeasonChange,
}: {
  seasons: readonly TalentConfigSeason[]
  selectedSeason: TalentConfigSeason
  snapshot: TalentConfigSnapshot | undefined
  onSeasonChange: (season: TalentConfigSeason) => void
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {seasons.map((season) => {
        const isActive = season === selectedSeason
        const count =
          snapshot?.seasons.find((entry) => entry.season === season)?.bosses
            .length ?? 0

        return (
          <Button
            key={season}
            size="sm"
            variant={isActive ? "default" : "outline"}
            onClick={() => onSeasonChange(season)}
            type="button"
          >
            {season}
            <span className="rounded-full bg-black/10 px-2 py-0.5 text-[0.7rem] text-current/80">
              {count}
            </span>
          </Button>
        )
      })}
    </div>
  )
}

function BossList({
  bosses,
  selectedBossId,
  onSelectBoss,
}: {
  bosses: TalentConfigBoss[]
  selectedBossId: string | null
  onSelectBoss: (bossId: string) => void
}) {
  if (bosses.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border/70 bg-card/35 px-4 py-6 text-sm text-muted-foreground">
        No bosses configured for the selected season yet.
      </div>
    )
  }

  return (
    <div className="grid gap-2">
      {bosses.map((boss) => {
        const isActive = boss.id === selectedBossId

        return (
          <div
            key={boss.id}
            className={`inline-card grid gap-2 text-left transition-all ${isActive ? "border-primary/30 bg-primary/8 shadow-[0_18px_36px_-30px_color-mix(in_oklch,var(--primary)_90%,transparent)]" : "bg-card/50 hover:border-primary/15 hover:bg-card/80"}`}
          >
            <div className="flex items-center justify-between gap-3">
              <Button
                unstyled
                className="min-w-0 flex-1 text-left"
                onClick={() => onSelectBoss(boss.id)}
                type="button"
              >
                <div className="truncate text-sm font-semibold text-foreground">
                  {boss.name}
                </div>
                <div className="text-kicker mt-1 text-[0.72rem] font-medium tracking-[0.16em] text-muted-foreground">
                  Order {boss.sortOrder}
                </div>
              </Button>
              <div className="flex items-center gap-2">
                <Tag className="bg-transparent px-2 py-1 text-[0.68rem]">
                  {boss.talents.length} talents
                </Tag>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Helper functions
function validateBossDraft(
  draft: NewBossDraft,
  snapshot: TalentConfigSnapshot | undefined,
): BossDraftValidation {
  const name = draft.name.trim()

  const nameError =
    name.length === 0
      ? "Boss name is required."
      : name.length < 2
        ? "Boss name should be at least 2 characters."
        : null

  const bossIdError = bossIdAlreadyExists(snapshot, draft)
    ? "A boss with this derived ID already exists."
    : null

  return {
    name: nameError,
    bossId: bossIdError,
  }
}

function bossIdAlreadyExists(
  snapshot: TalentConfigSnapshot | undefined,
  draft: NewBossDraft,
): boolean {
  if (!snapshot || !draft.name.trim()) {
    return false
  }

  return Boolean(
    snapshot.bossesById[
      getTalentConfigBossId({ season: draft.season, name: draft.name })
    ],
  )
}
